import { Entity } from '@api';
import { Tab } from '@design/components/tab-lines/TabLines';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useEarningsScenario } from '@terminal/react-query-hooks/earnings/useEarningsScenario';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { EarningsWidget } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/earnings-widget/EarningsWidget';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useSearchParams } from 'react-router';
import { useEarningsPreviewAndRecapInsights } from '../../hooks/useEarningsPreviewAndRecapInsights';
import { useEarningsStore } from '../../useEarningsStore/useEarningsStore';
import { EarningsFilters } from '../earnings-filters/EarningsFilters';
import { EarningsOverview } from '../earnings-overview/EarningsOverview';
import { EarningsTabPanel } from '../earnings-tab-panel/EarningsTabPanel';
import { EarningsTableWrapper } from '../earnings-table-wrapper/EarningsTableWrapper';
import { PreviewAndRecap } from '../preview-and-recap/PreviewAndRecap';
import {
  getDefaultActiveReportingPeriod,
  ReportingDates,
} from '../reporting-dates/ReportingDates';
import styles from './RevampEarningsContent.module.scss';

enum RevampEarningsTabKey {
  EarningsInsights = 'earnings-insights',
  History = 'history',
}

export interface RevampEarningsContentProps {
  entity: Entity;
}

export const RevampEarningsContent = ({
  entity,
}: RevampEarningsContentProps) => {
  const { t } = useTranslation(['earnings']);
  const { trackEarningsTabSelected } = usePosthogEvents();
  const { earningsData, earningExchange } = useEarningsStore();
  const [searchParams] = useSearchParams();
  const isAssetOverviewPage = !!useMatch(appPaths.assetOverview);
  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );
  const [activeReportingPeriod, setActiveReportingPeriod] = useState(() =>
    getDefaultActiveReportingPeriod(earningsData)
  );
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.has(queryKeys.epsFilter)
      ? RevampEarningsTabKey.History
      : RevampEarningsTabKey.EarningsInsights
  );

  useEffect(() => {
    setActiveReportingPeriod(getDefaultActiveReportingPeriod(earningsData));
  }, [earningsData]);

  // PreviewAndRecap can render either classic or KG insights.
  const { hasEarningsInsight, isInsightsAvailabilityLoading } =
    useEarningsPreviewAndRecapInsights(activeReportingPeriod.id);
  const shouldShowEarningsOverview =
    !isInsightsAvailabilityLoading && !hasEarningsInsight;
  const shouldContainInsightsScroll =
    isNewEarningsTabEnabled &&
    isAssetOverviewPage &&
    activeTab === RevampEarningsTabKey.EarningsInsights;

  const { data: earningsScenarioData } = useEarningsScenario({
    ticker: entity.ticker,
    exchange: earningExchange,
    year: activeReportingPeriod.fiscal_year,
    period: activeReportingPeriod.fiscal_period,
  });

  const tabs: Tab<RevampEarningsTabKey>[] = [
    {
      tabKey: RevampEarningsTabKey.EarningsInsights,
      tabLabel: t('earnings:tabs.earningsInsights'),
      renderPanel: (
        <div
          className={clsx(styles.insightsPanel, {
            [styles.containedScroll]: shouldContainInsightsScroll,
          })}
        >
          <ReportingDates
            reportingPeriods={earningsData}
            activeReportingPeriod={activeReportingPeriod}
            onActiveReportingPeriodChange={setActiveReportingPeriod}
          />
          {shouldShowEarningsOverview ? (
            <div
              className={clsx({
                [styles.scrollableContent]: shouldContainInsightsScroll,
              })}
            >
              <EarningsOverview activeReportingPeriod={activeReportingPeriod} />
              {earningsScenarioData && (
                <EarningsWidget
                  key={activeReportingPeriod.id}
                  earningsData={earningsScenarioData}
                  entity={entity}
                  source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
                />
              )}
            </div>
          ) : (
            <PreviewAndRecap
              key={activeReportingPeriod.id}
              earningsId={activeReportingPeriod.id}
              entityTag={entity.tag}
            />
          )}
        </div>
      ),
    },
    {
      tabKey: RevampEarningsTabKey.History,
      tabLabel: t('earnings:tabs.history'),
      renderPanel: (
        <div className={styles.insightsPanel}>
          <div className={styles.filterOptionsWrapper}>
            <EarningsFilters />
          </div>
          <div className={styles.tableWrapper}>
            <ErrorBoundaryWrapper showBorder={false}>
              <EarningsTableWrapper entity={entity} />
            </ErrorBoundaryWrapper>
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (searchParams.has(queryKeys.epsFilter)) {
      setActiveTab(RevampEarningsTabKey.History);
    }
  }, [searchParams]);

  const onTabChange = (_index: number, tabKey: RevampEarningsTabKey) => {
    trackEarningsTabSelected(
      POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      tabKey,
      entity.tag
    );
    setActiveTab(tabKey);
  };

  return (
    <div
      className={clsx(styles.root, {
        [styles.containedScroll]: shouldContainInsightsScroll,
      })}
    >
      <EarningsTabPanel
        tabs={tabs}
        activeTab={activeTab}
        onChange={onTabChange}
      />
    </div>
  );
};
